import {CommonModule} from '@angular/common';
import {
    afterNextRender,
    booleanAttribute,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    DestroyRef,
    ElementRef,
    inject,
    InjectionToken,
    Input,
    input,
    OnInit,
    output,
    Self,
    viewChild,
} from '@angular/core';
import {
    ControlValueAccessor,
    FormControl,
    FormsModule,
    NgControl,
    ReactiveFormsModule,
    Validators,
} from '@angular/forms';
import {NgbModule} from '@ng-bootstrap/ng-bootstrap';
import {v4 as uuidv4} from 'uuid';
import {ControlMessagesComponent} from '../control-messages/control-messages.component';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {PopoverIconComponent} from '../popover-icon/popover-icon.component';
import {trimIfString} from '@brunata-angular/util';

/**
 * App-level defaults for {@link InputTextGroupComponent}. Provide this token (e.g. in an app
 * module/bootstrap) to flip the component's value-handling behaviour for every instance in that
 * app, without setting the inputs on each usage. Per-instance inputs still win over this.
 *
 * - `trim`: when `true` (default) the emitted value is trimmed and empty becomes `null`. Set
 *   `false` to emit the raw value verbatim.
 * - `markTouchedOnChange`: when `true` the control is marked touched on every value change (so
 *   validation errors show while typing). Default `false` marks touched on blur only.
 * - `autoComplete`: the browser autocomplete attribute applied when a usage doesn't set
 *   `[autoComplete]`. Defaults to `'off'`; ngwebbill provides `'on'` to keep its legacy behaviour.
 */
export const INPUT_TEXT_GROUP_DEFAULTS = new InjectionToken<{
    trim?: boolean;
    markTouchedOnChange?: boolean;
    autoComplete?: 'on' | 'off';
}>('INPUT_TEXT_GROUP_DEFAULTS');

@Component({
    selector: 'app-input-text-group',
    templateUrl: './input-text-group.component.html',
    imports: [
        CommonModule,
        ReactiveFormsModule,
        FormsModule,
        NgbModule,
        ControlMessagesComponent,
        PopoverIconComponent,
    ],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InputTextGroupComponent implements OnInit, ControlValueAccessor {
    @Input() label = '';
    /** `password` masks the typed value; the rest of the component behaves identically. */
    readonly type = input<'text' | 'password'>('text');
    /**
     * When set, renders the Bootstrap grid layout (label in a `col-{labelCols}`, field in the rest)
     * instead of the default stacked layout. ngwebbill passes a value (0 = no label / full-width
     * input, >0 = offset grid) to match its legacy app-local component; other apps leave it unset.
     */
    @Input() labelCols: number | null = null;
    @Input() maxLength: number | null = null;
    @Input() placeholder = '';
    @Input() autoFocus = false;
    @Input() name = '';
    @Input() autoComplete: 'on' | 'off' | undefined;
    @Input() information: string | undefined;
    /**
     * Explicit required flag. When provided it wins over the validator-derived value, so ngwebbill
     * can drive the asterisk via `[required]` like its legacy component. When left undefined the
     * value is derived from a `Validators.required` on the control (other apps' behaviour).
     * `booleanAttribute` coerces a string `required="true"` attribute (ngwebbill has strict
     * templates off) to a real boolean so the asterisk isn't silently lost.
     */
    @Input({transform: booleanAttribute}) required: boolean | undefined;

    /** Trim the emitted value (empty -> null). Overrides the app-level default. */
    @Input() trim: boolean | undefined;
    /** Mark the control touched on every value change instead of on blur. Overrides the default. */
    @Input() markTouchedOnChange: boolean | undefined;

    inputBlur = output();

    private readonly defaults = inject(INPUT_TEXT_GROUP_DEFAULTS, {optional: true});

    formControl = new FormControl<string>('');
    id: string = uuidv4();

    private onChange: ((value: string | null) => void) | undefined;
    onTouch: (() => void) | undefined;

    private readonly input = viewChild.required<ElementRef<HTMLInputElement>>('input');

    constructor(
        @Self() public ngControl: NgControl,
        private changeDetectorRef: ChangeDetectorRef,
        private destroyRef: DestroyRef,
    ) {
        ngControl.valueAccessor = this;
        afterNextRender(() => {
            if (this.autoFocus) {
                this.input().nativeElement.focus();
            }
        });
    }

    get labelClass(): string {
        const required = this.required === true ? 'required' : '';
        // Grid layout adds the Bootstrap column class; the stacked layout uses just `required`.
        return this.labelCols != null ? `col-${this.labelCols} ${required}`.trim() : required;
    }

    get inputColsClass(): string {
        return this.labelCols != null && this.labelCols > 0 ? `col-${12 - this.labelCols}` : '';
    }

    get autoCompleteResolved(): 'on' | 'off' {
        return this.autoComplete ?? this.defaults?.autoComplete ?? 'off';
    }

    private get trimEnabled(): boolean {
        return this.trim ?? this.defaults?.trim ?? true;
    }

    private get touchOnChange(): boolean {
        return this.markTouchedOnChange ?? this.defaults?.markTouchedOnChange ?? false;
    }

    ngOnInit(): void {
        this.formControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(data => {
            this.changeDetectorRef.markForCheck();
            this.onChange?.(this.trimEnabled ? trimIfString(data) : data);
            if (this.touchOnChange) {
                this.ngControl.control?.markAsTouched();
            }
        });

        // Explicit [required] wins; otherwise derive from the control's required validator.
        this.required =
            this.required ?? this.ngControl.control?.hasValidator(Validators.required) ?? false;

        // Then using ChangeDetectionStrategy.OnPush the component doesn't notice, when the control is marked as
        // touched, and will therefore not show validation errors. However, Angular doesn't offer a simple way of
        // listening to, when a control is marked as touched, that we can hook into. So we have to reassign the
        // markAsTouched() method in order to call detectChanges().
        if (this.ngControl.control != null) {
            const originalMarkAsTouched = this.ngControl.control.markAsTouched.bind(
                this.ngControl.control,
            );
            const originalMarkAsUntouched = this.ngControl.control.markAsUntouched.bind(
                this.ngControl.control,
            );

            this.ngControl.control.markAsTouched = (...args): void => {
                originalMarkAsTouched(...args);
                this.changeDetectorRef.markForCheck();
            };

            this.ngControl.control.markAsUntouched = (...args): void => {
                originalMarkAsUntouched(...args);
                this.changeDetectorRef.markForCheck();
            };
        }

        // An async validator settles after the blur has rendered PENDING, and nothing else marks
        // this OnPush view then, so its error would stay hidden until the next blur or submit.
        this.ngControl.control?.statusChanges
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.changeDetectorRef.markForCheck());
    }

    registerOnChange(fn: (value: string | null) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouch = fn;
    }

    writeValue(value: string | null): void {
        if (value === null) {
            this.formControl.reset(null, {emitEvent: false});
            return;
        }

        this.formControl.setValue(value, {emitEvent: false});
    }

    setDisabledState(isDisabled: boolean): void {
        if (isDisabled) {
            this.formControl.disable({emitEvent: false});
        } else {
            this.formControl.enable({emitEvent: false});
        }
        this.changeDetectorRef.markForCheck();
    }

    protected onBlur(): void {
        this.onTouch?.();
        this.inputBlur.emit();
    }
}
